import {type RefObject, useCallback, useEffect, useRef, useState} from 'react';
import type {
    JitsiConference,
    JitsiConnection,
    JitsiLocalTrack,
    JitsiTrack,
    JitsiTrackState,
    ReceiverConstraints,
} from '../pages/stream/types/lib-jitsi-meet';
import useStreamingStore from '../store/useStreamingStore.ts';
import {buildOptions} from '../utils/StreamingUtil.ts';
import useCenterStore from '../store/useCenterStore.ts';

interface JitsiConnectionReturn {
    getConnection: () => Promise<void>;
    leave: () => Promise<void>;
    isConnecting: boolean;
    isConnected: boolean;
    studioTrack: JitsiTrackState;
    localTrack: RefObject<JitsiTrackState>;
    localTrackVersion: number;
    remoteTrackVersion: number;
    remoteTracks: RefObject<Map<string, JitsiTrackState>>;
    displayedParticipants: RefObject<Set<string>>;
    triggerConstraintUpdate: () => void;
}

type TTrackType = 'studio' | 'local' | 'remote';

const CONSTRAINT_EVAL_INTERVAL = 20000;

// [gs #70] 스튜디오는 창을 두 개(메인·모니터) 띄우고 각각 별도 연결로 같은 방에 들어온다.
// 모니터 창이 쓰는 이름이 '스튜디오_봇'이다(ssc-studio-client js/participants.js 의 NON_CENTER_NAMES).
// 이 이름을 걸러내지 않으면 경로당 TV가 스튜디오 모니터링 봇을 경로당으로 보고 그리드 한 칸을 내준다.
// 표시명 문자열 계약이라 스튜디오 쪽 이름이 바뀌면 여기도 함께 바뀌어야 한다 —
// 근본 해결은 토큰의 role 클레임으로 판정하는 것이고 서버 변경이 필요해 별건이다.
const STUDIO_MONITOR_BOT_NAME = '스튜디오_봇';

const useJitsiConnection = (): JitsiConnectionReturn => {
    const [isConnecting, setIsConnecting] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const {centerInfo, mediaStream, videoMode} = useCenterStore();
    const {state, setStreamingState, setIsSplash, gridLayout, setGridLayout, setConnectionError} = useStreamingStore();
    const [studioTrack, setStudioTrack] = useState<JitsiTrackState>({partId: '', videoTrack: null, audioTrack: null});
    const localTrack = useRef<JitsiTrackState>({partId: '', videoTrack: null, audioTrack: null});
    const remoteTracks = useRef<Map<string, JitsiTrackState>>(new Map());
    // 지금 셀에 배치된 참여자 id. owner는 RemoteCell이며 여기선 읽기만 한다.
    const displayedParticipants = useRef<Set<string>>(new Set());
    const [localTrackVersion, setLocalTrackVersion] = useState(0);
    const [remoteTrackVersion, setRemoteTrackVersion] = useState(0);
    const [constraintsTrigger, setConstraintsTrigger] = useState(0);

    // leave()로 인한 의도적 종료 중엔 CONNECTION_DISCONNECTED 리스너가 connectionError를
    // true로 되돌리지 못하게 막는다(비동기 disconnect()와의 set(true)/set(false) 레이스 방지).
    const intentionalLeaveRef = useRef(false);

    const updateLocalTracks = useCallback(
        async (newMediaStream: MediaStream) => {
            try {
                if (!state.conference) return;
                if (localTrack.current.videoTrack) {
                    await state.conference.removeTrack(localTrack.current.videoTrack);
                    localTrack.current.videoTrack.dispose();
                    localTrack.current.videoTrack = null;
                }
                if (localTrack.current.audioTrack) {
                    await state.conference.removeTrack(localTrack.current.audioTrack);
                    localTrack.current.audioTrack.dispose();
                    localTrack.current.audioTrack = null;
                }
                const videoTracks = newMediaStream.getVideoTracks();
                const audioTracks = newMediaStream.getAudioTracks();
                let newVideoTrack: JitsiLocalTrack | null = null;
                let newAudioTrack: JitsiLocalTrack | null = null;
                if (videoTracks.length > 0) {
                    newVideoTrack = await window.JitsiMeetJS.createLocalTracks({
                        devices: ['video'],
                        micDeviceId: undefined,
                        cameraDeviceId: videoTracks[0].getSettings().deviceId,
                        resolution: 180,
                    }).then((tracks) => tracks.find((track) => track.getType() === 'video') || null);
                    if (newVideoTrack) {
                        await state.conference.addTrack(newVideoTrack);
                    }
                }
                if (audioTracks.length > 0) {
                    newAudioTrack = await window.JitsiMeetJS.createLocalTracks({
                        devices: ['audio'],
                        micDeviceId: audioTracks[0].getSettings().deviceId,
                        cameraDeviceId: undefined,
                    }).then((tracks) => tracks.find((track) => track.getType() === 'audio') || null);
                    if (newAudioTrack) {
                        await state.conference.addTrack(newAudioTrack);
                        await newAudioTrack.mute();
                    }
                }
                localTrack.current.audioTrack = newAudioTrack;
                localTrack.current.videoTrack = newVideoTrack;
                setLocalTrackVersion((v) => v + 1);
            } catch (error) {
                console.error('로컬 트랙 업데이트 중 오류:', error);
            }
        },
        [state.conference]
    );

    useEffect(() => {
        if (mediaStream && state.conference && isConnected) {
            updateLocalTracks(mediaStream);
        }
    }, [mediaStream, state.conference, isConnected]);

    const updateBridgeConstraints = useCallback(() => {
        if (!state.conference || !isConnected) return;

        const constraintsMap: Record<string, { maxHeight: number; minHeight?: number }> = {};
        const selectedSources: string[] = [];

        if (studioTrack.videoTrack) {
            const studioTrackName = studioTrack.videoTrack.getSourceName();
            selectedSources.push(studioTrackName);
            constraintsMap[studioTrackName] = {
                maxHeight: 720,
            };
        }

        // 화면에 배치된 참여자만 요청 — 레이아웃 밖 참여자는 영상을 아예 받지 않는다
        for (const partId of displayedParticipants.current) {
            const trackState = remoteTracks.current.get(partId);
            if (!trackState?.videoTrack) continue;
            const sourceName = trackState.videoTrack.getSourceName();
            selectedSources.push(sourceName);
            constraintsMap[sourceName] = {maxHeight: 90};
        }

        const constraints: ReceiverConstraints = {
            lastN: -1,
            // 명시하지 않은 소스는 0 → lastN과 무관하게 브리지가 포워딩하지 않는다
            defaultConstraints: {maxHeight: 0},
            constraints: constraintsMap,
            selectedSources,
            ...(studioTrack.videoTrack ? {onStageSources: [studioTrack.videoTrack.getSourceName()]} : {}),
        };

        console.log(
            `🔧 [CONSTRAINTS] lastN=${constraints.lastN}, ` +
            `selected=${selectedSources.length}, displayed=${displayedParticipants.current.size}, ` +
            `remote=${remoteTracks.current.size}`
        );
        state.conference.setReceiverConstraints(constraints);
    }, [state.conference, isConnected, studioTrack.videoTrack]);

    useEffect(() => {
        const timer = setTimeout(() => {
            updateBridgeConstraints();
        }, 500);
        return () => clearTimeout(timer);
    }, [updateBridgeConstraints, constraintsTrigger, remoteTrackVersion]);

    useEffect(() => {
        if (!isConnected || !state.conference) return;
        const interval = setInterval(() => {
            setConstraintsTrigger((v) => v + 1);
        }, CONSTRAINT_EVAL_INTERVAL);
        return () => clearInterval(interval);
    }, [isConnected, state.conference]);

    const handleStudioTrack = useCallback((track: JitsiTrack, participantId: string, trackType: 'video' | 'audio') => {
        if (trackType === 'video') {
            setStudioTrack((prevState) => {
                if (prevState.videoTrack) return prevState;
                return {...prevState, partId: participantId, videoTrack: track};
            });
            // updateBridgeConstraints가 studioTrack.videoTrack 변경을 감지하여 자동 반영되지만
            // 즉시 적용을 위해 trigger
            setConstraintsTrigger((v) => v + 1);
        } else {
            setStudioTrack((prevState) => {
                if (prevState.audioTrack) return prevState;
                return {...prevState, audioTrack: track};
            });
        }
    }, []);

    const handleLocalTrack = useCallback((track: JitsiTrack, participantId: string, trackType: 'video' | 'audio') => {
        if (trackType === 'video') {
            if (localTrack.current.videoTrack) {
                return;
            }
            localTrack.current.partId = participantId;
            localTrack.current.videoTrack = track;
        } else {
            if (localTrack.current.audioTrack) {
                return;
            }
            localTrack.current.partId = participantId;
            localTrack.current.audioTrack = track;
        }
    }, []);

    const handleRemoteTrack = useCallback((track: JitsiTrack, participantId: string, trackType: 'video' | 'audio') => {
        const foundRemoteTrack = remoteTracks.current.get(participantId);
        if (!foundRemoteTrack) {
            const newTrack: JitsiTrackState = {partId: '', videoTrack: null, audioTrack: null};
            if (trackType === 'video') {
                newTrack.videoTrack = track;
            } else {
                // audio
                newTrack.audioTrack = track;
            }
            newTrack.partId = participantId;
            remoteTracks.current.set(participantId, newTrack);
        } else {
            if (trackType === 'video') {
                foundRemoteTrack.videoTrack = track;
            } else {
                foundRemoteTrack.audioTrack = track;
            }
            foundRemoteTrack.partId = participantId;
        }
        setRemoteTrackVersion((v) => v + 1);
    }, []);

    const handleTrackAdded = useCallback(
        (track: JitsiTrack) => {
            if (!track || typeof track.getType !== 'function' || typeof track.attach !== 'function') {
                console.error('Invalid track object:', track);
                return;
            }

            const trackType = track.getType();
            const participantId = track.getParticipantId();
            const participant = participantId ? track.conference?.participants.get(participantId) : null;
            const displayName = participant?.getDisplayName() || 'Unknown';
            const mediaTrack = (track as any).getTrack?.() as MediaStreamTrack | undefined;

            console.log(
                `🎯 [TRACK_ADDED] type=${trackType}, participantId=${participantId}, ` +
                `displayName="${displayName}", isLocal=${track.isLocal()}, ` +
                `mediaTrack=${mediaTrack ? `${mediaTrack.readyState}(${mediaTrack.id})` : 'null'}, ` +
                `→ ${displayName === '스튜디오' ? 'STUDIO' : track.isLocal() ? 'LOCAL' : 'REMOTE'}`
            );

            if (displayName === '스튜디오') {
                handleStudioTrack(track, participantId!, trackType);
            } else if (displayName === STUDIO_MONITOR_BOT_NAME) {
                // 스튜디오 모니터링 봇은 경로당이 아님 — 그리드 후보에서 제외
                return;
            } else if (track.isLocal()) {
                handleLocalTrack(track, participantId!, trackType);
            } else {
                handleRemoteTrack(track, participantId!, trackType);
            }

            if (!track.isLocal() && displayName !== '스튜디오') {
                console.log(`🎯 [REMOTE_TRACKS] 현재 총 ${remoteTracks.current.size}개, ` +
                    `video 있는 트랙: ${Array.from(remoteTracks.current.values()).filter(t => t.videoTrack).length}개`);
            }
        },
        [handleLocalTrack, handleRemoteTrack, handleStudioTrack]
    );

    const clearTrackState = (trackState: TTrackType) => {
        switch (trackState) {
            case 'studio':
                setStudioTrack({partId: '', videoTrack: null, audioTrack: null});
                return;
            case 'local':
                localTrack.current = {partId: '', videoTrack: null, audioTrack: null};
                return;
            case 'remote':
            default:
                return;
        }
    };

    const removeSpecificTrack = (trackState: TTrackType, type: 'video' | 'audio') => {
        switch (trackState) {
            case 'studio':
                if (type === 'video') {
                    setStudioTrack((prevState) => ({
                        ...prevState,
                        videoTrack: null,
                    }));
                } else {
                    setStudioTrack((prevState) => ({
                        ...prevState,
                        audioTrack: null,
                    }));
                }
                return;
            case 'local':
                if (type === 'video') {
                    localTrack.current.videoTrack = null;
                } else {
                    localTrack.current.audioTrack = null;
                }
                return;
            case 'remote':
            default:
                return;
        }
    };

    const handleTrackRemoved = useCallback((track: JitsiTrack) => {
        const participantId = track.getParticipantId();
        const trackType = track.getType();

        if (track.isLocal()) {
            if (localTrack.current.partId === participantId) {
                if (!localTrack.current.videoTrack && !localTrack.current.audioTrack) {
                    clearTrackState('local');
                } else {
                    removeSpecificTrack('local', trackType);
                }
            }
        } else {
            // displayName으로 스튜디오 판별 (stale closure 회피)
            const participant = track.conference?.participants.get(participantId);
            const displayName = participant?.getDisplayName() || 'Unknown';

            if (displayName === '스튜디오') {
                removeSpecificTrack('studio', trackType);
            } else {
                const foundRemoteTrack = remoteTracks.current.get(participantId);
                if (foundRemoteTrack) {
                    if (trackType === 'video') {
                        foundRemoteTrack.videoTrack = null;
                    } else {
                        foundRemoteTrack.audioTrack = null;
                    }
                    if (!foundRemoteTrack.videoTrack && !foundRemoteTrack.audioTrack) {
                        remoteTracks.current.delete(participantId);
                    }
                }
                setRemoteTrackVersion((v) => v + 1);
            }
        }
        track.dispose();
    }, []);

    const createLocalTracks = useCallback(async (conference: JitsiConference) => {
        const {videoInputD, audioInputD} = useCenterStore.getState();

        const localTracks = await window.JitsiMeetJS.createLocalTracks({
            devices: ['audio', 'video'],
            resolution: 180,
            cameraDeviceId: videoInputD?.deviceId,
            micDeviceId: audioInputD?.deviceId,
        });
        for (const track of localTracks) {
            await conference.addTrack(track);
            if (track.getType() === 'video') {
                localTrack.current.videoTrack = track;
            } else {
                localTrack.current.audioTrack = track;
                localTrack.current.audioTrack.mute();
            }
        }
        setLocalTrackVersion((v) => v + 1);
        return localTracks;
    }, []);

    const createOnConferenceJoined = useCallback(
        (conference: JitsiConference, username: string) => {
            return async () => {
                setIsConnected(true);
                setIsConnecting(false);
                // 재연결 성공 — '연결 끊김' 오버레이를 내리고 15초 재시도 루프를 멈춘다.
                setConnectionError(false);
                if (conference && username) {
                    conference.setDisplayName(username);
                }
                if (conference) {
                    // 셀 배치 전엔 아무 참여자 영상도 받지 않는다(배치 후 updateBridgeConstraints가 허용)
                    conference.setReceiverConstraints({
                        lastN: -1,
                        defaultConstraints: {maxHeight: 0},
                    });
                    // mediaStream이 이미 있으면 useEffect의 updateLocalTracks가 처리하므로 스킵
                    const currentMediaStream = useCenterStore.getState().mediaStream;
                    if (!currentMediaStream) {
                        try {
                            await createLocalTracks(conference);
                        } catch (error) {
                            console.error('createOnConferenceJoined 에러', error);
                        }
                    }
                }
            };
        },
        [createLocalTracks, setConnectionError]
    );

    const onConferenceLeft = useCallback(() => {
        setIsConnected(false);
    }, []);

    const onUserJoined = useCallback((id: string) => {
        console.log('user joined!', id);
    }, []);

    const onUserLeft = useCallback((id: string) => {
        console.log('user left!', id);
        const foundRemoteTrack = remoteTracks.current.get(id);
        if (foundRemoteTrack) {
            try {
                foundRemoteTrack.videoTrack?.dispose();
                foundRemoteTrack.audioTrack?.dispose();
            } catch (e) {
                console.warn('track dispose 중 오류:', e);
            }
            remoteTracks.current.delete(id);
            setRemoteTrackVersion((v) => v + 1);
        }
    }, []);

    const onMicrophoneChanged = useCallback((isMuted: boolean) => {
        console.log('Microphone changed:', isMuted);
    }, []);

    const onAVModerationChanged = useCallback((isMuted: boolean) => {
        console.log('Mic AV Moderation changed:', isMuted);
    }, []);

    const onAVModerationApproved = useCallback(() => {
        setIsSplash(true);
        setGridLayout({mode: 'direct-chat', sub: true});
        localTrack.current.audioTrack?.unmute();
        setTimeout(() => {
            setIsSplash(false);
        }, 3000);
    }, [localTrack, setIsSplash, setGridLayout, gridLayout]);

    const onTrackMuteChanged = useCallback(
        (track: JitsiTrack) => {
            if (track.isLocal() && track.isMuted()) {
                setIsSplash(true);
                setGridLayout({mode: '28', sub: true});
                setTimeout(() => {
                    setIsSplash(false);
                }, 3000);
            }
        },
        [videoMode, setIsSplash, setGridLayout]
    );

    const onParticipantConnStatusChanged = useCallback(
        (id: string, status: string) => {
            console.log(`참가자(${id}) 연결 상태: ${status}`);
        },
        []
    );

    const onConferenceFailed = useCallback((error: unknown) => {
        // [gs4] CONFERENCE_FAILED 미등록 → isConnecting 영구 고착으로 재연결 불가 문제 해소
        console.error('Jitsi CONFERENCE_FAILED', error);
        setIsConnecting(false);
        setIsConnected(false);
        setStreamingState({connection: undefined, conference: undefined});
        setConnectionError(true);
    }, [setStreamingState, setConnectionError]);

    const setupConferenceListeners = useCallback(
        (conference: JitsiConference, username: string) => {
            const onConferenceJoined = createOnConferenceJoined(conference, username);
            conference.on(window.JitsiMeetJS.events.conference.TRACK_ADDED, handleTrackAdded);
            conference.on(window.JitsiMeetJS.events.conference.TRACK_REMOVED, handleTrackRemoved);
            conference.on(window.JitsiMeetJS.events.conference.CONFERENCE_JOINED, onConferenceJoined);
            conference.on(window.JitsiMeetJS.events.conference.CONFERENCE_LEFT, onConferenceLeft);
            conference.on(window.JitsiMeetJS.events.conference.CONFERENCE_FAILED, onConferenceFailed);
            conference.on(window.JitsiMeetJS.events.conference.USER_JOINED, onUserJoined);
            conference.on(window.JitsiMeetJS.events.conference.USER_LEFT, onUserLeft);
            conference.on(window.JitsiMeetJS.events.conference.AUDIO_UNMUTE_PERMISSIONS_CHANGED, onMicrophoneChanged);
            conference.on(window.JitsiMeetJS.events.conference.AV_MODERATION_CHANGED, onAVModerationChanged);
            conference.on(window.JitsiMeetJS.events.conference.AV_MODERATION_APPROVED, onAVModerationApproved);
            conference.on(window.JitsiMeetJS.events.conference.TRACK_MUTE_CHANGED, onTrackMuteChanged);
            conference.on(
                window.JitsiMeetJS.events.conference.PARTICIPANT_CONN_STATUS_CHANGED,
                onParticipantConnStatusChanged
            );
        },
        [
            createOnConferenceJoined,
            handleTrackAdded,
            handleTrackRemoved,
            onConferenceLeft,
            onConferenceFailed,
            onUserJoined,
            onUserLeft,
            onMicrophoneChanged,
            onAVModerationChanged,
            onAVModerationApproved,
            onTrackMuteChanged,
            onParticipantConnStatusChanged,
        ]
    );

    const onConnectionSuccess = useCallback(
        async (connection: JitsiConnection) => {
            try {
                const conference = connection.initJitsiConference(state.room, {
                    userInfo: {
                        displayName: centerInfo?.name || '-',
                    },
                });
                setStreamingState({
                    conference: conference,
                    username: centerInfo?.name,
                });
                setupConferenceListeners(conference, centerInfo?.name || '-');
                conference.join();
                console.log('컨퍼런스 조인 성공!');
            } catch (error) {
                console.error('Jitsi onConnectionSuccess Fail', error);
                setIsConnecting(false);
            }
        },
        [centerInfo?.name, setStreamingState, setupConferenceListeners]
    );

    const onConnectionFailed = useCallback((error: any) => {
        console.error('Jitsi onConnectionFailed Fail', error);
        setIsConnecting(false);
        setIsConnected(false);
        // 연결 실패 시 connection state 정리
        setStreamingState({
            connection: undefined,
            conference: undefined,
        });
        setConnectionError(true);
    }, [setStreamingState, setConnectionError]);

    const onConnectionDisconnected = useCallback(() => {
        console.log('Jitsi onConnectionDisconnected Fail');
        setIsConnected(false);
        setIsConnecting(false);
        setStreamingState({
            connection: undefined,
            conference: undefined,
        });
        // leave()가 유발한 의도적 종료면 가드를 여기서 소비하고 '연결 끊김' 오버레이를 띄우지 않는다.
        // (finally에서 즉시 리셋하지 않는 이유: disconnect() 이벤트는 나중 태스크에서 도착하므로
        // 가드가 이 핸들러까지 살아있어야 진짜 레이스를 잡는다.)
        if (intentionalLeaveRef.current) {
            intentionalLeaveRef.current = false;
        } else {
            setConnectionError(true);
        }
    }, [setStreamingState, setConnectionError]);

    const getConnection = useCallback(async () => {
        if (isConnecting || isConnected || !state.appId || !state.room || !state.jwt) return;
        try {
            // fresh-start 보정: 이전 leave()가 DISCONNECTED 이벤트 없이 끝난 경우를 대비해
            // 새 연결 시작 시점에 가드를 리셋한다 — 다음 진짜 단절까지 가드가 남아있으면 안 됨.
            intentionalLeaveRef.current = false;
            // 기존 연결이 남아있으면 먼저 정리
            if (state.connection) {
                try {
                    state.connection.disconnect();
                } catch (e) {
                    console.warn('기존 연결 정리 중 오류:', e);
                }
                setStreamingState({connection: undefined, conference: undefined});
            }
            setIsConnecting(true);
            const options = buildOptions(state.appId, state.room);
            const connection = new window.JitsiMeetJS.JitsiConnection(null, state.jwt, options);
            connection.addEventListener(window.JitsiMeetJS.events.connection.CONNECTION_ESTABLISHED, () =>
                onConnectionSuccess(connection)
            );
            connection.addEventListener(window.JitsiMeetJS.events.connection.CONNECTION_FAILED, onConnectionFailed);
            connection.addEventListener(
                window.JitsiMeetJS.events.connection.CONNECTION_DISCONNECTED,
                onConnectionDisconnected
            );
            setStreamingState({connection});
            connection.connect();
        } catch (error) {
            console.error('Jitsi connect Fail', error);
            setIsConnecting(false);
        }
    }, [
        isConnecting,
        isConnected,
        state.appId,
        state.room,
        state.jwt,
        setStreamingState,
        onConnectionSuccess,
        onConnectionFailed,
        onConnectionDisconnected,
    ]);

    const leave = useCallback(async () => {
        // 의도적 종료 시작 — CONNECTION_DISCONNECTED 리스너의 set(true)를 무력화한다.
        intentionalLeaveRef.current = true;
        setConnectionError(false);
        try {
            // 기존 로컬 트랙 dispose
            if (localTrack.current.videoTrack) {
                localTrack.current.videoTrack.dispose();
                localTrack.current.videoTrack = null;
            }
            if (localTrack.current.audioTrack) {
                localTrack.current.audioTrack.dispose();
                localTrack.current.audioTrack = null;
            }

            const {state} = useStreamingStore.getState();
            if (state.conference) {
                await state.conference.leave();
            }
            if (state.connection) {
                state.connection.disconnect();
            }
            setIsConnected(false);
            setIsConnecting(false);
            setStreamingState({
                connection: undefined,
                conference: undefined,
            });
            // disconnect() 완료 후에도 재확정 — 레이스로 리스너가 먼저 돌았더라도 최종값은 false.
            setConnectionError(false);
        } catch (error) {
            console.error('Jitsi disconnect Fail', error);
        }
        // intentionalLeaveRef는 여기서 리셋하지 않는다: disconnect()는 await되지 않으므로
        // CONNECTION_DISCONNECTED 이벤트가 나중 태스크에서 도착한다. 가드를 여기서 내리면
        // 그 이벤트가 onConnectionDisconnected에서 setConnectionError(true)로 스토어를 재오염시킨다.
        // 가드는 onConnectionDisconnected(이벤트 핸들러)에서 소비하거나, 다음 getConnection() 시작 시
        // fresh-start로 리셋한다.
    }, [setStreamingState, setConnectionError]);

    const triggerConstraintUpdate = useCallback(() => {
        setConstraintsTrigger((v) => v + 1);
    }, []);

    return {
        getConnection,
        leave,
        isConnecting,
        isConnected,
        studioTrack,
        localTrack,
        localTrackVersion,
        remoteTrackVersion,
        remoteTracks,
        displayedParticipants,
        triggerConstraintUpdate,
    };
};

export default useJitsiConnection;
